import React, { useState } from 'react';
import { Box, Text, VStack, Flex, IconButton } from '@chakra-ui/react';
import { FiMinus, FiPlus } from 'react-icons/fi';
import { Campaign } from '@foodfluence/shared';
import { getMaxPartySize } from '../utils/partySizeUtils';
import { wizardTokens } from '../styles';

interface PartySizeSelectorProps {
  campaign?: Campaign;
  selectedSize: number | null;
  // null clears the selection (tapping an already-selected option toggles it off).
  onSizeSelect: (size: number | null) => void;
}

// Quick-pick rows cover the common cases (1 or 2); anything larger is entered via the stepper.
const CARD_SIZES = [1, 2] as const;
const STEPPER_MIN = 3;
const labelForSize = (size: number) => (size === 1 ? 'Just me' : `Me + ${size - 1}`);
const guestsLabel = (size: number) => `${size} guest${size > 1 ? 's' : ''}`;

// Board BookVisit step 1 option row: radio dot · label + hint · big condensed count.
const ON = { border: `2px solid ${wizardTokens.ink}`, bg: '#F1FADF' };
const OFF = { border: '1px solid #DCDCD8', bg: '#FFFFFF' };

const Dot: React.FC<{ on: boolean }> = ({ on }) => (
  <Box
    as="span"
    flex="none"
    w="22px"
    h="22px"
    boxSizing="border-box"
    borderRadius="999px"
    border={on ? `7px solid ${wizardTokens.ink}` : '2px solid #9A9A9E'}
    bg="#FFFFFF"
  />
);

const rowProps = (on: boolean) => ({
  w: '100%',
  minH: '72px',
  p: '12px 16px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  color: wizardTokens.ink,
  cursor: 'pointer',
  textAlign: 'left' as const,
  ...(on ? ON : OFF),
});

const PartySizeSelector: React.FC<PartySizeSelectorProps> = ({
  campaign,
  selectedSize,
  onSizeSelect,
}) => {
  const maxPartySize = getMaxPartySize(campaign);
  const cardSizes = CARD_SIZES.filter((s) => s <= maxPartySize);
  // Gate on the campaign being loaded (not the raw field) so the stepper doesn't flash in on the
  // loading default and then vanish; once loaded, the resolved max governs — including the
  // default (3) when the brand left maxPartySize unset.
  const showStepper = !!campaign && maxPartySize >= STEPPER_MIN;
  // Solo-only brand: a single "Just me" row and a standing note that the cap is 1.
  const soloOnly = campaign?.maxPartySize === 1;

  const stepperSelected = selectedSize !== null && selectedSize >= STEPPER_MIN;
  const stepperValue = stepperSelected ? selectedSize! : STEPPER_MIN;

  const stepTo = (next: number) => onSizeSelect(Math.min(maxPartySize, Math.max(STEPPER_MIN, next)));

  // Shown when the creator tries to push past the brand's cap (the brand's max governs — there is
  // no separate platform ceiling).
  const [showLimitNotice, setShowLimitNotice] = useState(false);
  const atMax = stepperValue >= maxPartySize;
  const limitMessage = `This brand can host parties of up to ${maxPartySize}.`;

  const handleIncrement = () => {
    if (atMax) { setShowLimitNotice(true); return; }
    setShowLimitNotice(false);
    stepTo(stepperValue + 1);
  };
  const handleDecrement = () => {
    setShowLimitNotice(false);
    stepTo(stepperValue - 1);
  };

  const hintFor = (size: number) =>
    size === maxPartySize ? 'The most this campaign allows' : size === 1 ? 'Solo visit' : guestsLabel(size - 1);

  const stepBtn = {
    size: 'sm',
    w: '36px',
    h: '36px',
    minW: '36px',
    borderRadius: '999px',
    bg: '#FFFFFF',
    border: '1.5px solid #0E0E0E',
    color: wizardTokens.ink,
    _hover: { bg: '#F2F2F0' },
    _disabled: { borderColor: '#DCDCD8', color: '#9A9A9E', cursor: 'not-allowed', opacity: 1 },
  } as const;

  return (
    <VStack role="radiogroup" aria-label="Party size" spacing="10px" align="stretch" fontFamily="display">
      {cardSizes.map((size) => {
        const isSelected = selectedSize === size;
        return (
          <Box
            as="button"
            type="button"
            role="radio"
            aria-checked={isSelected}
            key={size}
            onClick={() => onSizeSelect(isSelected ? null : size)}
            {...rowProps(isSelected)}
          >
            <Dot on={isSelected} />
            <Flex flex={1} direction="column" gap="2px" minW={0}>
              <Text fontSize="17px" fontWeight={600} lineHeight="normal">{labelForSize(size)}</Text>
              <Text fontSize="13px" color="#55555A" lineHeight="normal">{hintFor(size)}</Text>
            </Flex>
            <Text textStyle="display" fontSize="27px" lineHeight={1.12} aria-hidden>{size}</Text>
          </Box>
        );
      })}

      {soloOnly && (
        <Text fontSize="13px" fontWeight={600} color="#A1260F">{limitMessage}</Text>
      )}

      {showStepper && (
        <>
          <Box
            role="radio"
            aria-checked={stepperSelected}
            tabIndex={0}
            onClick={() => onSizeSelect(stepperSelected ? null : stepperValue)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSizeSelect(stepperSelected ? null : stepperValue);
              }
            }}
            {...rowProps(stepperSelected)}
          >
            <Dot on={stepperSelected} />
            <Flex flex={1} direction="column" gap="2px" minW={0}>
              <Text fontSize="17px" fontWeight={600} lineHeight="normal">More than 2?</Text>
              <Text fontSize="13px" color="#55555A" lineHeight="normal">
                {stepperSelected ? guestsLabel(stepperValue) : `Up to ${maxPartySize} guests`}
              </Text>
            </Flex>
            <Flex align="center" gap="10px" flexShrink={0}>
              <IconButton
                aria-label="Fewer guests"
                icon={<FiMinus size={16} strokeWidth={2.6} />}
                isDisabled={stepperValue <= STEPPER_MIN}
                onClick={(e) => { e.stopPropagation(); handleDecrement(); }}
                {...stepBtn}
              />
              <Text textStyle="display" fontSize="27px" lineHeight={1.12} minW="20px" textAlign="center">
                {stepperValue}
              </Text>
              <IconButton
                aria-label="More guests"
                icon={<FiPlus size={16} strokeWidth={2.6} />}
                onClick={(e) => { e.stopPropagation(); handleIncrement(); }}
                {...stepBtn}
              />
            </Flex>
          </Box>
          {showLimitNotice && (
            <Text fontSize="13px" fontWeight={600} color="#A1260F">{limitMessage}</Text>
          )}
        </>
      )}
    </VStack>
  );
};

export default PartySizeSelector;
